/**
 * Persistent banner informing legacy users about Smart Intake migration.
 */
import { ActionIcon, Button, Group } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconWand, IconX } from "@tabler/icons-react";
import { useState } from "react";

import { useAuthStore } from "@utils/stores/authStore";
import { gradientTextStyle } from "@/utils/brandStyles";
import { AppRoutes } from "@/constants.ts";
import {
  canOwnerUpgradeToIntakeFromBanner,
  getIntakeLoginDestination,
  getIntakeMigrationPrerequisiteDestination,
  isOwnerIntakeMigrationEligible,
  shouldShowLegacyDeprecationBanner,
  withFrontendMode,
} from "@/utils/frontendMigration";

import {
  postFrontendModeChoice,
  FrontendMigrationContractRequiredError,
} from "./frontendMigrationApi";
import {
  MIGRATION_BANNER_BRAND_LABEL,
  MIGRATION_BANNER_SUBTITLE,
  MIGRATION_MODAL_GRADIENT,
  MIGRATION_MODAL_GRADIENT_COLORS,
} from "./frontendMigrationModalTheme";
import { useFrontendMigrationUiStore } from "./frontendMigrationStore";

import "./LegacyDeprecationBanner.scss";

/** Card surface styles applied inline so Mantine Paper defaults cannot override them. */
const BANNER_SURFACE_STYLE = {
  padding: 14,
  border: "1px solid #dfe3ee",
  borderRadius: 14,
  backgroundColor: "#f1f3fc",
  boxShadow: "none",
} as const;

export function LegacyDeprecationBanner() {
  const user = useAuthStore((state) => state.user);
  const billingContext = useAuthStore((state) => state.billingContext);
  const refreshAuth = useAuthStore((state) => state.refreshAuth);
  const updateFrontendMode = useAuthStore((state) => state.updateFrontendMode);
  const dismissed = useFrontendMigrationUiStore(
    (state) => state.bannerDismissed
  );
  const dismissBanner = useFrontendMigrationUiStore(
    (state) => state.dismissBanner
  );
  const [loading, setLoading] = useState(false);

  const isVisible = Boolean(
    user && !dismissed && shouldShowLegacyDeprecationBanner(user)
  );

  if (!isVisible || !user) {
    return null;
  }

  const canUpgrade = canOwnerUpgradeToIntakeFromBanner(user);
  const isPendingOnboarding = user.frontendMode === "intake_pending_onboarding";

  const handleOpenIntake = async () => {
    if (isPendingOnboarding) {
      window.location.assign(AppRoutes.gettingStarted);
      return;
    }

    if (canUpgrade) {
      if (!isOwnerIntakeMigrationEligible(user)) {
        window.location.assign(
          getIntakeMigrationPrerequisiteDestination(billingContext)
        );
        return;
      }
      setLoading(true);
      let persistedMode: "intake" | null = null;
      try {
        persistedMode = await postFrontendModeChoice("intake");
        updateFrontendMode(persistedMode);
      } catch (error) {
        if (error instanceof FrontendMigrationContractRequiredError) {
          window.location.assign(
            getIntakeMigrationPrerequisiteDestination(billingContext)
          );
          return;
        }
        const message =
          error instanceof Error && error.message
            ? error.message
            : "We couldn't switch your business to the new SmartWiz. Please try again.";
        notifications.show({
          title: "Could not update",
          message,
          color: "red",
        });
        return;
      } finally {
        setLoading(false);
      }

      try {
        await refreshAuth();
      } catch {
        // Backend choice already persisted; redirect using the saved mode.
      }

      const refreshedUser = useAuthStore.getState().user ?? user;
      window.location.assign(
        getIntakeLoginDestination(
          withFrontendMode(refreshedUser, persistedMode ?? "intake")
        )
      );
    }
  };

  return (
    <div className="legacy-deprecation-banner-container">
      <div
        className="legacy-deprecation-banner"
        role="status"
        style={BANNER_SURFACE_STYLE}
      >
        <Group
          className="legacy-deprecation-banner__eyebrow"
          justify="space-between"
          wrap="nowrap"
        >
          <Group gap={6} wrap="nowrap">
            <div className="legacy-deprecation-banner__icon" aria-hidden>
              <IconWand size={14} stroke={1.8} />
            </div>
            <span className="legacy-deprecation-banner__eyebrow-label">
              New version
            </span>
          </Group>
          <ActionIcon
            aria-label="Dismiss migration reminder"
            className="legacy-deprecation-banner__close"
            color="gray"
            variant="subtle"
            size="sm"
            onClick={dismissBanner}
          >
            <IconX size={14} />
          </ActionIcon>
        </Group>
        <p className="legacy-deprecation-banner__title">
          <span style={gradientTextStyle(MIGRATION_MODAL_GRADIENT)}>
            {MIGRATION_BANNER_BRAND_LABEL}
          </span>{" "}
          is here
        </p>
        <p className="legacy-deprecation-banner__subtitle">
          {MIGRATION_BANNER_SUBTITLE}
        </p>
        <Button
          classNames={{ root: "legacy-deprecation-banner__button" }}
          fullWidth
          gradient={MIGRATION_MODAL_GRADIENT_COLORS}
          loading={loading}
          radius="md"
          size="xs"
          variant="gradient"
          onClick={() => void handleOpenIntake()}
        >
          {isPendingOnboarding ? "Continue setup" : "Update now"}
        </Button>
      </div>
    </div>
  );
}
